import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScheduledMeetingsMutations } from '../contexts';
import { usePopupBlocked } from '../contexts/PopupBlockedContext';
import { SCHEDULED_MEETINGS_QUERY_KEYS } from '../contexts/ScheduledMeetingsContext';
import { useSettings } from '../contexts/SettingsContext/hooks/useSettings';
import { useUser } from '../contexts/UserContext';
import { authRedirectService } from '../services/authRedirectService';
import { isHostedByTeamHub } from '../services/hostAuthService';
import {
  type MeetingWindowHandle,
  reserveMeetingWindow,
} from '../services/meetingWindow/meetingWindowManager';
import { openMeetingInCurrentTab } from '../services/meetingWindow/openMeetingInCurrentTab';
import { openMeetingInExternalWindow } from '../services/meetingWindow/openMeetingInExternalWindow';
import { assertUnreachable } from '../utils/assertUnreachable';
import { isLegacyTeamHubDesktop, isTeamHubDesktop } from '../utils/desktopApp';
import { createMeetingAccessPayload } from '../utils/meetingAccessPayload';
import { buildMeetingPath } from '../utils/url';
import useApiManager from './useApiManager';
import { useToastHelpers } from './useToast';

interface UseStartMeetingOptions {
  trackInteraction?: (name: string) => () => void;
}

export const useStartMeeting = (options?: UseStartMeetingOptions) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const apiManager = useApiManager();
  const { profile } = useSettings();
  const { create: createMeeting, isCreating } = useScheduledMeetingsMutations();
  const { showError } = useToastHelpers();
  const { promptPopupBlocked } = usePopupBlocked();
  const {
    isAuthenticated,
    idToken,
    userEmail,
    login: logIn,
    getIdToken,
    getAccessToken,
  } = useUser();

  const startMeeting = useCallback(async () => {
    if (isCreating) {
      return;
    }

    const getAuth = async () => ({
      idToken: await getIdToken(),
      accessToken: await getAccessToken(),
    });

    // Hosted mode: reserve the popup window NOW, inside the click gesture, so
    // the browser doesn't block it after the async create/fetch below. The
    // window is pointed at the resolved meeting URL once we know the room id.
    // Current desktop builds intercept this reservation and open a main-process
    // window while keeping the postMessage auth/settings bridge; older ones have
    // no such window and skip the reservation altogether (SM-1186).
    let meetingWindow: MeetingWindowHandle | null = null;
    let popupBlocked = false;
    let openExternally = false;
    if (isHostedByTeamHub()) {
      if (isLegacyTeamHubDesktop()) {
        // Desktop shells without their own meeting window must not be asked to
        // reserve one: the `about:blank` reservation reaches the OS and Windows
        // turns it into a Store prompt. Skip the reservation entirely and hand
        // the room over once it exists — they route it to the system browser
        // (SM-1186). Nothing is opened until the create succeeds, so a failure
        // leaves no stray window behind.
        openExternally = true;
      } else {
        const reservation = reserveMeetingWindow({ getAuth });
        switch (reservation.status) {
          case 'existing':
            // A meeting window is already open — focus it and don't start another.
            return;
          case 'reserved':
            meetingWindow = reservation.handle;
            break;
          case 'blocked':
            // Can't prompt yet — the room id isn't known until the meeting is
            // created below, so defer the fallback prompt to `launch`.
            popupBlocked = true;
            break;
          default:
            assertUnreachable(reservation);
        }
      }
    }

    const launch = (room: string, password?: string | null): void => {
      if (meetingWindow) {
        meetingWindow.navigate(room, password);
      } else if (openExternally) {
        openMeetingInExternalWindow(room, password);
      } else if (popupBlocked) {
        // Don't silently open the call in the host tab — let the user choose.
        // The fallback is a full-page navigation to NG Meet's own origin (so
        // Jitsi's external_api.js loads), NOT an in-page (memory-router)
        // navigation inside the TeamHub shell.
        promptPopupBlocked({
          meetingId: room,
          password,
          getAuth,
          // Never offer the desktop app a full-page navigation: it has no
          // chrome to come back with, so the TeamHub shell would be gone until
          // the user quits and relaunches.
          onOpenInPage: isTeamHubDesktop()
            ? undefined
            : () => openMeetingInCurrentTab(room, password),
        });
      } else {
        navigate(buildMeetingPath(room, password));
      }
    };

    const endTracking = options?.trackInteraction?.('start_new_meeting');

    try {
      const defaultMeetingId = profile?.defaultMeetingId?.trim();

      if (defaultMeetingId) {
        const schedule = await queryClient.fetchQuery({
          queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.detail(defaultMeetingId),
          queryFn: () => apiManager.ownership.getSchedule(defaultMeetingId),
        });
        launch(defaultMeetingId, schedule?.password);
        return;
      }

      const userDomain = userEmail?.split('@')[1] || '';
      const result = await createMeeting({
        password: '',
        ...createMeetingAccessPayload('standard', userDomain),
      });
      launch(result.room, result.password);
    } catch (error) {
      meetingWindow?.cancel();
      console.error('Failed to start new meeting:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to start new meeting';
      showError('Unable to create meeting', errorMessage);
    } finally {
      endTracking?.();
    }
  }, [
    apiManager,
    profile?.defaultMeetingId,
    createMeeting,
    navigate,
    queryClient,
    showError,
    options,
    isCreating,
    userEmail,
    getIdToken,
    getAccessToken,
    promptPopupBlocked,
  ]);

  const startMeetingUnauth = useCallback(() => {
    authRedirectService.savePreLoginAction({ type: 'start_meeting' });
    logIn();
  }, [logIn]);

  useEffect(() => {
    if (!(isAuthenticated && idToken)) {
      return;
    }
    const action = authRedirectService.consumePreLoginAction();
    if (action?.type === 'start_meeting') {
      startMeeting();
    }
  }, [isAuthenticated, idToken, startMeeting]);

  return { startMeeting, startMeetingUnauth, isCreating };
};
